/* eslint-disable no-unused-vars */
import { AlertTriangle, X } from 'lucide-react';
import React, { useState } from 'react';
import { deleteOrganization, deleteOrganizationProject } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type DeleteConfirmDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

/**
 * Shared confirmation dialog for the two destructive delete actions
 * (project, organization/workspace). Requires typing the exact resource
 * name before the delete button enables — deliberately more friction than
 * a plain "are you sure?" modal, since a stray click or double-submit here
 * is much more expensive than elsewhere in the app: it soft-deletes every
 * release, runtime config, and (for organizations) API key scoped to the
 * resource.
 */
function DeleteConfirmDialog({ actionDialogReturn, onNavigate }: DeleteConfirmDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const { activeProjectId, setActiveProjectId, selectOrganization, refreshWorkspace } =
    useWorkspace();
  const [confirmText, setConfirmText] = useState('');

  const isDeleteProject = actionDialog?.kind === 'delete-project';
  const isDeleteOrganization = actionDialog?.kind === 'delete-organization';

  if (!actionDialog || (!isDeleteProject && !isDeleteOrganization)) {
    return null;
  }

  const targetName = isDeleteProject ? actionDialog.projectName : actionDialog.organizationName;
  const targetLabel = isDeleteProject ? 'project' : 'workspace';
  const canSubmit = confirmText.trim() === targetName.trim() && !dialogBusy;

  const close = () => {
    if (!dialogBusy) {
      setActionDialog(null);
      setConfirmText('');
    }
  };

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || dialogBusy || !canSubmit) return;

    setDialogBusy(true);
    setDialogError('');
    try {
      if (isDeleteProject) {
        await deleteOrganizationProject(
          session.accessToken,
          actionDialog.organizationId,
          actionDialog.projectId
        );
        if (activeProjectId === actionDialog.projectId) {
          setActiveProjectId('');
        }
      } else if (isDeleteOrganization) {
        await deleteOrganization(session.accessToken, actionDialog.organizationId);
        // Force re-selection of a remaining organization — the one just
        // deleted is no longer a valid active context.
        selectOrganization('');
        onNavigate('/', { replace: true });
      }

      refreshWorkspace();
      toast('success', isDeleteProject ? 'Project deleted' : 'Workspace deleted');
      setActionDialog(null);
      setConfirmText('');
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker" style={{ color: 'var(--danger, #dc2626)' }}>
              Danger zone
            </div>
            <h3>Delete {targetLabel}</h3>
            <p>
              This deactivates <strong>{targetName}</strong>
              {isDeleteProject
                ? ' and every release, runtime config, and lane scoped to it.'
                : ' and every project, team, member, release, runtime config, and API key scoped to it.'}
              {' '}This cannot be undone from the admin UI.
            </p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <div className="settingsGroup">
            <div
              className="fieldHint"
              style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}
            >
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                Type <strong>{targetName}</strong> to confirm.
              </span>
            </div>
            <label className="settingsField">
              <span>{isDeleteProject ? 'Project name' : 'Workspace name'}</span>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={targetName}
                autoFocus
              />
            </label>
          </div>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>
            Cancel
          </button>
          <button
            type="submit"
            className="primaryButton"
            style={canSubmit ? { background: 'var(--danger, #dc2626)' } : undefined}
            disabled={dialogBusy || !canSubmit}
          >
            {dialogBusy ? 'Deleting…' : `Delete ${targetLabel}`}
          </button>
        </div>
      </form>
    </div>
  );
}

export { DeleteConfirmDialog };
